export interface LocalsGuideEntry {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  description: string;
  insiderNote?: string;
  tags: string[];
  priceRange?: string;
  distance?: string;
  status: 'active' | 'removed';
}

export interface LocalsGuideSection {
  id: string;
  title: string;
  icon: string;
  color: string;
  description: string;
  entries: LocalsGuideEntry[];
}

export const localsGuideSections: LocalsGuideSection[] = [
  {
    id: 'dining',
    title: 'Best Dining',
    icon: '🍽️',
    color: '#4CC9FF',
    description: 'Resident-recommended restaurants worth the reservation — from quiet weeknight dinners to celebratory evenings.',
    entries: [
      {
        id: 'dining-1',
        name: 'Estiatorio Milos',
        category: 'Dining',
        subcategory: 'Mediterranean',
        description: 'Exceptional Greek seafood in the Cosmopolitan. The whole fish is flown in daily. Quiet enough for conversation, refined without being stiff.',
        insiderNote: 'Ask for a table near the kitchen side — better light and less foot traffic from the casino floor.',
        tags: ['Seafood', 'Greek', 'Cosmopolitan', 'Fine Dining'],
        priceRange: '$$$',
        distance: '10 min',
        status: 'active',
      },
      {
        id: 'dining-2',
        name: 'Bavette\'s Steakhouse',
        category: 'Dining',
        subcategory: 'Steakhouse',
        description: 'A moody, old-school steakhouse at Park MGM. Dark leather, great cocktails, and one of the better dry-aged ribeyes in the city.',
        insiderNote: 'The bar seats are underrated — great for solo dining or a quick pre-show dinner.',
        tags: ['Steakhouse', 'Park MGM', 'Classic'],
        priceRange: '$$$',
        distance: '12 min',
        status: 'active',
      },
      {
        id: 'dining-3',
        name: 'Sparrow + Wolf',
        category: 'Dining',
        subcategory: 'Modern American',
        description: 'A local favorite off the Strip. Chef Brian Howard\'s menu is inventive without being precious. One of the few places in Vegas that feels like a real neighborhood restaurant.',
        insiderNote: 'Locals eat here. The tasting menu is worth it if you have time.',
        tags: ['Local', 'Modern American', 'Off-Strip', 'Chef-Driven'],
        priceRange: '$$$',
        distance: '15 min',
        status: 'active',
      },
      {
        id: 'dining-4',
        name: 'Lotus of Siam',
        category: 'Dining',
        subcategory: 'Thai',
        description: 'Widely considered the best Thai restaurant in the United States. Unassuming strip-mall location, extraordinary Northern Thai cooking.',
        insiderNote: 'Order from the Northern Thai menu — it\'s separate and worth asking for. Reservations essential.',
        tags: ['Thai', 'Local Legend', 'Northern Thai'],
        priceRange: '$$',
        distance: '20 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'nightlife',
    title: 'Best Nightlife',
    icon: '🌃',
    color: '#9B7FFF',
    description: 'For when you want to experience Las Vegas on your own terms — curated, not chaotic.',
    entries: [
      {
        id: 'nightlife-1',
        name: 'Marquee Dayclub / Nightclub',
        category: 'Nightlife',
        subcategory: 'Club',
        description: 'The Cosmopolitan\'s flagship club. Well-run, excellent production, and close enough to walk back from.',
        insiderNote: 'Table service is the move here. The bungalows at Dayclub are worth the premium.',
        tags: ['Club', 'Cosmopolitan', 'Table Service'],
        priceRange: '$$$$',
        distance: '10 min',
        status: 'active',
      },
      {
        id: 'nightlife-2',
        name: 'Allegiant Stadium Concerts',
        category: 'Nightlife',
        subcategory: 'Live Music',
        description: 'World-class acts in a genuinely impressive venue. Rideshare from Panorama is straightforward.',
        insiderNote: 'Arrive 30 minutes early — security lines move fast but parking is a nightmare. Rideshare drop-off is smooth.',
        tags: ['Concerts', 'Stadium', 'Live Music'],
        priceRange: '$$$',
        distance: '15 min',
        status: 'active',
      },
      {
        id: 'nightlife-3',
        name: 'The Sphere',
        category: 'Nightlife',
        subcategory: 'Entertainment',
        description: 'A genuinely new kind of entertainment experience. Worth seeing at least once — and likely more than once.',
        insiderNote: 'Seats in the 200s offer the best balance of immersion and comfort. Avoid the very front rows for your first visit.',
        tags: ['Sphere', 'Immersive', 'Entertainment', 'Las Vegas'],
        priceRange: '$$$',
        distance: '12 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'wellness',
    title: 'Best Wellness & Recovery',
    icon: '🧘',
    color: '#2DD4A0',
    description: 'Recovery, restoration, and premium wellness options that Las Vegas residents actually use.',
    entries: [
      {
        id: 'wellness-1',
        name: 'Qua Baths & Spa at Caesars',
        category: 'Wellness',
        subcategory: 'Spa',
        description: 'One of the most complete spa experiences in the city. Roman baths, arctic ice room, and a full treatment menu. Worth a half-day.',
        insiderNote: 'The thermal circuit alone is worth the day pass. Book treatments mid-week for better availability.',
        tags: ['Spa', 'Caesars', 'Thermal', 'Recovery'],
        priceRange: '$$$',
        distance: '8 min',
        status: 'active',
      },
      {
        id: 'wellness-2',
        name: 'Remedy IV Therapy',
        category: 'Wellness',
        subcategory: 'IV Therapy',
        description: 'Mobile IV therapy with in-unit service. Popular with residents for post-travel recovery and general wellness.',
        insiderNote: 'Example wellness service — verify current providers and availability directly.',
        tags: ['IV Therapy', 'Mobile', 'Recovery', 'In-Unit'],
        priceRange: '$$',
        distance: 'In-unit',
        status: 'active',
      },
      {
        id: 'wellness-3',
        name: 'Cryotherapy at CryoFit',
        category: 'Wellness',
        subcategory: 'Cryotherapy',
        description: 'Whole-body cryotherapy and localized treatment. Useful for athletic recovery and general inflammation.',
        insiderNote: 'Multiple locations in the valley. The Summerlin location tends to be less crowded.',
        tags: ['Cryo', 'Recovery', 'Athletic'],
        priceRange: '$$',
        distance: '20 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'fitness',
    title: 'Best Fitness',
    icon: '💪',
    color: '#F0A832',
    description: 'Gyms, studios, and fitness options that go beyond the building gym.',
    entries: [
      {
        id: 'fitness-1',
        name: 'Life Time Las Vegas',
        category: 'Fitness',
        subcategory: 'Full-Service Gym',
        description: 'The most complete gym facility in the valley. Pool, spa, tennis, group classes, and premium equipment. Worth the membership if you use it regularly.',
        insiderNote: 'The Henderson location is newer and typically less crowded than the Summerlin flagship.',
        tags: ['Gym', 'Pool', 'Classes', 'Premium'],
        priceRange: '$$$',
        distance: '25 min',
        status: 'active',
      },
      {
        id: 'fitness-2',
        name: 'F45 Training',
        category: 'Fitness',
        subcategory: 'Group Training',
        description: 'High-intensity functional training in 45-minute group sessions. Multiple locations across the valley.',
        insiderNote: 'The Downtown Summerlin location has a strong regular community.',
        tags: ['HIIT', 'Group Training', 'F45'],
        priceRange: '$$',
        distance: '20 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'shopping',
    title: 'Best Shopping',
    icon: '🛍️',
    color: '#FF6B9D',
    description: 'Where Panorama residents actually shop — from everyday essentials to considered purchases.',
    entries: [
      {
        id: 'shopping-1',
        name: 'Crystals at CityCenter',
        category: 'Shopping',
        subcategory: 'Luxury Retail',
        description: 'The best luxury retail concentration in Las Vegas. Louis Vuitton, Hermès, Prada, and more in a genuinely beautiful space.',
        insiderNote: 'Weekday mornings are the best time — minimal crowds and attentive service.',
        tags: ['Luxury', 'CityCenter', 'Designer', 'Retail'],
        priceRange: '$$$$',
        distance: '10 min',
        status: 'active',
      },
      {
        id: 'shopping-2',
        name: 'Whole Foods Market — Town Square',
        category: 'Shopping',
        subcategory: 'Grocery',
        description: 'The most convenient premium grocery option for Panorama residents. Reliable quality and a good prepared foods section.',
        insiderNote: 'The Town Square location has better parking than the Spring Valley store.',
        tags: ['Grocery', 'Whole Foods', 'Everyday'],
        priceRange: '$$',
        distance: '10 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'services',
    title: 'Best Services',
    icon: '⭐',
    color: '#4CC9FF',
    description: 'Trusted local service providers recommended by Panorama residents.',
    entries: [
      {
        id: 'services-1',
        name: 'Concierge-Level Dry Cleaning',
        category: 'Services',
        subcategory: 'Dry Cleaning',
        description: 'Several dry cleaners in the area offer pickup and delivery. Residents have recommended services that handle high-end garments with care.',
        insiderNote: 'Ask in the community for current resident recommendations — preferences vary.',
        tags: ['Dry Cleaning', 'Pickup', 'Delivery'],
        priceRange: '$$',
        distance: 'Varies',
        status: 'active',
      },
      {
        id: 'services-2',
        name: 'Mobile Car Detailing',
        category: 'Services',
        subcategory: 'Auto',
        description: 'Mobile detailing services that come to the building. Several residents use in-garage detailing services.',
        insiderNote: 'Coordinate with building management for garage access. Some detailers are familiar with the building.',
        tags: ['Auto', 'Detailing', 'Mobile', 'In-Building'],
        priceRange: '$$',
        distance: 'In-building',
        status: 'active',
      },
    ],
  },
  {
    id: 'guests',
    title: 'Best for Guests Visiting',
    icon: '🏨',
    color: '#9B7FFF',
    description: 'Where to send guests, what to recommend, and how to make their Las Vegas visit memorable.',
    entries: [
      {
        id: 'guests-1',
        name: 'The Cosmopolitan of Las Vegas',
        category: 'Guests',
        subcategory: 'Hotel',
        description: 'The best hotel for guests who want a premium Strip experience without the mega-resort chaos. Excellent restaurants, great rooms, walkable to everything.',
        insiderNote: 'The Terrace One Bedroom suites are worth the upgrade for guests who want space.',
        tags: ['Hotel', 'Strip', 'Premium', 'Guest Recommendation'],
        priceRange: '$$$',
        distance: '10 min',
        status: 'active',
      },
      {
        id: 'guests-2',
        name: 'Helicopter Tour of the Strip',
        category: 'Guests',
        subcategory: 'Experience',
        description: 'A 15-minute night flight over the Strip is one of the most impressive things you can do with out-of-town guests.',
        insiderNote: 'Book the sunset or night flight — daytime is less impressive. Multiple operators available.',
        tags: ['Helicopter', 'Tour', 'Night Flight', 'Experience'],
        priceRange: '$$$',
        distance: '20 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'hidden-gems',
    title: 'Best Hidden Gems',
    icon: '💎',
    color: '#2DD4A0',
    description: 'The places that don\'t make the tourist lists but that residents return to.',
    entries: [
      {
        id: 'gems-1',
        name: 'Mordeo Boutique Wine Bar',
        category: 'Hidden Gems',
        subcategory: 'Wine Bar',
        description: 'A quiet, well-curated wine bar in the Arts District. Excellent natural wine selection and small plates. Feels nothing like the Strip.',
        insiderNote: 'The Arts District is worth exploring on a weekday evening — several good spots within walking distance.',
        tags: ['Wine Bar', 'Arts District', 'Local', 'Natural Wine'],
        priceRange: '$$',
        distance: '15 min',
        status: 'active',
      },
      {
        id: 'gems-2',
        name: 'Red Rock Canyon',
        category: 'Hidden Gems',
        subcategory: 'Outdoors',
        description: 'Thirty minutes from Panorama and a completely different world. The scenic loop is excellent for a morning drive or hike.',
        insiderNote: 'Go early — the park gets crowded by 10am on weekends. Weekday mornings are ideal.',
        tags: ['Outdoors', 'Hiking', 'Red Rock', 'Nature'],
        priceRange: '$',
        distance: '30 min',
        status: 'active',
      },
    ],
  },
  {
    id: 'second-home',
    title: 'Best for Second-Home Owners',
    icon: '🔑',
    color: '#F0A832',
    description: 'Resources and recommendations specifically useful for residents who split time between Las Vegas and another home.',
    entries: [
      {
        id: 'second-1',
        name: 'Trusted Property Check Services',
        category: 'Second Home',
        subcategory: 'Property Management',
        description: 'Several residents use property check services when away for extended periods. These services inspect the unit, handle minor issues, and provide peace of mind.',
        insiderNote: 'Ask in the community for current resident recommendations. Building staff can also assist with basic checks.',
        tags: ['Property Check', 'Second Home', 'Absentee Owner'],
        priceRange: '$$',
        distance: 'In-building',
        status: 'active',
      },
      {
        id: 'second-2',
        name: 'USPS Informed Delivery',
        category: 'Second Home',
        subcategory: 'Mail Management',
        description: 'Free USPS service that emails you daily scans of incoming mail. Essential for second-home owners who want visibility on mail without being present.',
        insiderNote: 'Set this up before your next extended absence. Takes 5 minutes at usps.com.',
        tags: ['Mail', 'USPS', 'Second Home', 'Remote Management'],
        priceRange: 'Free',
        distance: 'Online',
        status: 'active',
      },
    ],
  },
];

export function getLocalsGuideSectionById(id: string): LocalsGuideSection | undefined {
  return localsGuideSections.find((s) => s.id === id);
}
